import React from "react";

import BarChartIcon from "@mui/icons-material/BarChart";
import StorageIcon from "@mui/icons-material/Storage";
import WebIcon from "@mui/icons-material/Web";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import { FEATURE_CARD_ACCENT_WIDTH } from "@shell/styles/designTokens";

import { StackType } from "../../types";

interface Props {
  value: StackType | null;
  onChange: (type: StackType) => void;
}

interface TypeOption {
  type: StackType;
  label: string;
  subtitle: string;
  description: string;
  icon: React.ReactNode;
  examples: string;
}

const TYPE_OPTIONS: TypeOption[] = [
  {
    type: StackType.DATA_ENGINEERING,
    label: "Data Engineering",
    subtitle: "Analytics & data warehousing",
    description:
      "Deploy a fully managed data engineering stack with a SQL query engine, object storage, and BI tool.",
    icon: <StorageIcon sx={{ fontSize: 40 }} />,
    examples: "Trino + S3 + Beaver, Spark + GCS + Superset",
  },
  {
    type: StackType.ANALYTICAL,
    label: "Analytical",
    subtitle: "Dashboards & reporting",
    description:
      "Deploy a lightweight analytics stack with an OLAP engine and BI dashboard connected to your existing database.",
    icon: <BarChartIcon sx={{ fontSize: 40 }} />,
    examples: "ClickHouse + Metabase + PostgreSQL, DuckDB + Superset + MySQL",
  },
  {
    type: StackType.FRONTEND_BACKEND,
    label: "Web Application",
    subtitle: "Full-stack web application",
    description:
      "Deploy a complete web application with a frontend framework, API backend, and database.",
    icon: <WebIcon sx={{ fontSize: 40 }} />,
    examples: "React + FastAPI + PostgreSQL, Next.js + NestJS + MongoDB",
  },
];

export function StepChooseType({ value, onChange }: Props) {
  const theme = useTheme();

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Choose a stack type
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select the kind of infrastructure stack you want to deploy.
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        {TYPE_OPTIONS.map((opt) => {
          const selected = value === opt.type;
          return (
            <Paper
              key={opt.type}
              variant="outlined"
              role="button"
              tabIndex={0}
              aria-label={`Select ${opt.label}`}
              onClick={() => onChange(opt.type)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onChange(opt.type);
                }
              }}
              sx={{
                flex: 1,
                p: 2,
                cursor: "pointer",
                borderRadius: 1,
                border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
                borderTopWidth: selected ? FEATURE_CARD_ACCENT_WIDTH : 1,
                borderTopColor: selected
                  ? theme.palette.primary.main
                  : theme.palette.divider,
                bgcolor: selected
                  ? alpha(theme.palette.primary.main, 0.03)
                  : "background.paper",
                transition:
                  "border-color 0.15s, background-color 0.15s, transform 0.15s",
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  transform: "translateY(-2px)",
                },
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: theme.palette.primary.main,
                  outlineOffset: 2,
                },
              }}
            >
              <Box
                sx={{
                  color: selected ? "primary.main" : "text.secondary",
                  mb: 1,
                }}
              >
                {opt.icon}
              </Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {opt.label}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {opt.subtitle}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {opt.description}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                e.g. {opt.examples}
              </Typography>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
}
